import type { FC } from 'react';
import { useContext } from 'react';
import { Url } from '@wix/da-url';
import { useTranslation } from 'react-i18next';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import VisuallyHidden from '@wix/da-a11y/pkg/components/VisuallyHidden';
import WixelLogoAndText from '@wix/da-ds/pkg/WixelLogoAndText';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import { BiData, type DefaultBiEvent } from '@wix/da-bi/pkg/events';
import PlaygroundEmptyStateDesktop from '../../PlaygroundEmptyStateDesktop';
import { navigateToWixel } from '../../../../actions/studio/playground';
import PlaygroundEmptyStateMobile from '../../PlaygroundEmptyStateMobile';

interface Props {}

const WixelEmptyState: FC<Props> = () => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const dispatchNavigateToWixel = useAction(navigateToWixel);

  return isMobile ? (
    <PlaygroundEmptyStateMobile
      heading={t('playground.emptyState.wixel.heading.mobile', {
        postProcess: ['reactPostprocessor'],
        alt: (
          <VisuallyHidden>
            {t('playground.emptyState.wixel.heading.mobile.alt')}
          </VisuallyHidden>
        ),
        logo: <WixelLogoAndText />,
      })}
      description={t('playground.emptyState.wixel.description.mobile')}
    />
  ) : (
    <PlaygroundEmptyStateDesktop
      logo={<WixelLogoAndText />}
      heading={t('playground.emptyState.wixel.heading.desktop')}
      description={t('playground.emptyState.wixel.description.desktop')}
      callToAction={
        <SolidButton
          size="medium"
          variant="main"
          onClick={dispatchNavigateToWixel}
          biData={BiData<DefaultBiEvent>({
            evid: 2,
            click_info: 'get_started_wixel_empty_state',
          })}
        >
          {t('playground.emptyState.wixel.ctaLabel')}
        </SolidButton>
      }
      disclaimer={t('playground.emptyState.wixel.desktop.disclaimer.text', {
        postProcess: ['reactPostprocessor'],
        termsOfService: (
          <a
            className="text-link-level-2-tertiary"
            target="_blank"
            rel="noopener noreferrer"
            href={t(
              'playground.emptyState.wixel.desktop.disclaimer.termsLink.url'
            )}
          >
            {t('playground.emptyState.wixel.desktop.disclaimer.termsLink.text')}
          </a>
        ),
        privacyPolicy: (
          <a
            className="text-link-level-2-tertiary"
            target="_blank"
            rel="noopener noreferrer"
            href={t(
              'playground.emptyState.wixel.desktop.disclaimer.privacyLink.url'
            )}
          >
            {t(
              'playground.emptyState.wixel.desktop.disclaimer.privacyLink.text'
            )}
          </a>
        ),
      })}
      postfix={
        <img
          alt=""
          style={{
            width: '420px',
          }}
          src={Url.staticCDNLink(
            'eclipse/studio/playgroundemptystates/wixel-preview.jpg'
          )}
        />
      }
    />
  );
};

WixelEmptyState.displayName = 'WixelEmptyState';

export default WixelEmptyState;
